import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import adminApi from '@/lib/adminApi';
import { artistProfilePath } from '@/lib/artistSlug';
import { collaboratorProfilePath } from '@/lib/collaboratorSlug';

const formatTimestamp = (value) => {
  if (!value) return '—';
  try {
    return new Date(value).toLocaleString();
  } catch (error) {
    return value;
  }
};

const AdminEnquiries = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedConversationId, setExpandedConversationId] = useState('');
  const [replyTextByThread, setReplyTextByThread] = useState({});
  const [selectedMessageIdsByThread, setSelectedMessageIdsByThread] = useState({});
  const [busyReplyId, setBusyReplyId] = useState('');
  const [busyHideMessagesId, setBusyHideMessagesId] = useState('');
  const [showDeleted, setShowDeleted] = useState(false);
  const [selectedThreadIds, setSelectedThreadIds] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);

  const loadConversations = async () => {
    const response = await adminApi.get(`/admin/enquiries${showDeleted ? '?include_deleted=true' : ''}`);
    setConversations(response.data.conversations || []);
    setSelectedThreadIds([]);
  };

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const response = await adminApi.get(`/admin/enquiries${showDeleted ? '?include_deleted=true' : ''}`);
        if (mounted) setConversations(response.data.conversations || []);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [showDeleted]);

  const allVisibleThreadsSelected =
    conversations.length > 0 && conversations.every((conversation) => selectedThreadIds.includes(conversation.id));

  const toggleThreadSelection = (id, checked) => {
    setSelectedThreadIds((current) => (checked ? Array.from(new Set([...current, id])) : current.filter((item) => item !== id)));
  };

  const toggleAllVisibleThreads = () => {
    setSelectedThreadIds(allVisibleThreadsSelected ? [] : conversations.map((conversation) => conversation.id).filter(Boolean));
  };

  const handleBulkThreadAction = async (action) => {
    if (selectedThreadIds.length === 0) return;
    const confirmText =
      action === 'permanent_delete'
        ? `Permanently delete ${selectedThreadIds.length} selected deleted PMB thread(s)? This cannot be undone.`
        : action === 'restore'
          ? `Undo delete for ${selectedThreadIds.length} selected PMB thread(s)?`
          : `Move ${selectedThreadIds.length} selected PMB thread(s) to Deleted Threads?`;
    if (!window.confirm(confirmText)) return;
    setBulkBusy(true);
    try {
      await adminApi.post('/admin/bulk/enquiries', { ids: selectedThreadIds, action });
      await loadConversations();
    } finally {
      setBulkBusy(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this PMB thread from admin monitoring? You can restore it from Deleted Threads. Artist and collaborator inboxes will keep the conversation.')) return;
    await adminApi.delete(`/admin/enquiries/${id}`);
    await loadConversations();
  };

  const handleRestore = async (id) => {
    await adminApi.post(`/admin/enquiries/${id}/restore`);
    await loadConversations();
  };

  const handlePermanentDelete = async (id) => {
    if (!window.confirm('Permanently delete this PMB thread from the deleted list? This cannot be undone.')) return;
    await adminApi.delete(`/admin/enquiries/${id}/permanent`);
    await loadConversations();
  };

  const toggleMessageSelection = (conversationId, messageId, checked) => {
    setSelectedMessageIdsByThread((current) => {
      const currentIds = current[conversationId] || [];
      const nextIds = checked
        ? Array.from(new Set([...currentIds, messageId]))
        : currentIds.filter((id) => id !== messageId);
      return { ...current, [conversationId]: nextIds };
    });
  };

  const toggleAllMessages = (conversationId, messages) => {
    const ids = (messages || []).map((entry) => entry.id).filter(Boolean);
    setSelectedMessageIdsByThread((current) => {
      const currentIds = current[conversationId] || [];
      const allSelected = ids.length > 0 && ids.every((id) => currentIds.includes(id));
      return { ...current, [conversationId]: allSelected ? [] : ids };
    });
  };

  const handleHideSelectedMessages = async (conversationId) => {
    const selectedIds = selectedMessageIdsByThread[conversationId] || [];
    if (selectedIds.length === 0) return;
    if (!window.confirm(`Hide ${selectedIds.length} selected PMB message(s) from admin monitoring? They will remain visible to the artist and collaborator.`)) return;

    setBusyHideMessagesId(conversationId);
    try {
      await adminApi.post(`/admin/enquiries/${conversationId}/messages/hide`, { message_ids: selectedIds });
      setSelectedMessageIdsByThread((current) => ({ ...current, [conversationId]: [] }));
      await loadConversations();
    } finally {
      setBusyHideMessagesId('');
    }
  };

  const handleAdminReply = async (conversationId) => {
    const message = (replyTextByThread[conversationId] || '').trim();
    if (!message) return;

    setBusyReplyId(conversationId);
    try {
      await adminApi.post(`/admin/enquiries/${conversationId}/messages`, { message });
      setReplyTextByThread((current) => ({ ...current, [conversationId]: '' }));
      await loadConversations();
    } finally {
      setBusyReplyId('');
    }
  };

  return (
    <div className="space-y-6">
      <section className="bg-white rounded-[2rem] shadow-lg p-6 sm:p-8 border border-orange-100">
        <h2 className="text-3xl font-bold text-gray-900">Enquiry management</h2>
        <p className="text-gray-600 mt-3">
          Monitor PMB threads and continue admin profile-review messages with artists.
        </p>
        <div className="mt-5 flex flex-wrap gap-3">
          <button type="button" onClick={() => setShowDeleted(false)} className={`${!showDeleted ? 'btn-primary' : 'btn-outline'}`}>
            Active Threads
          </button>
          <button type="button" onClick={() => setShowDeleted(true)} className={`${showDeleted ? 'btn-primary' : 'btn-outline'}`}>
            Deleted Threads
          </button>
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-3 rounded-2xl border border-orange-100 bg-orange-50/50 p-3">
          <button type="button" onClick={toggleAllVisibleThreads} className="btn-outline px-4 py-2 text-sm" disabled={conversations.length === 0}>
            {allVisibleThreadsSelected ? 'Clear all visible' : 'Select all visible'}
          </button>
          {!showDeleted ? (
            <button
              type="button"
              onClick={() => handleBulkThreadAction('delete')}
              className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-600 hover:bg-red-50 disabled:opacity-50"
              disabled={bulkBusy || selectedThreadIds.length === 0}
            >
              Delete selected
            </button>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleBulkThreadAction('restore')}
                className="rounded-full border border-emerald-200 px-4 py-2 text-sm font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50"
                disabled={bulkBusy || selectedThreadIds.length === 0}
              >
                Undo selected
              </button>
              <button
                type="button"
                onClick={() => handleBulkThreadAction('permanent_delete')}
                className="rounded-full border border-red-200 px-4 py-2 text-sm font-bold text-red-700 hover:bg-red-50 disabled:opacity-50"
                disabled={bulkBusy || selectedThreadIds.length === 0}
              >
                Permanent delete selected
              </button>
            </>
          )}
          <span className="text-sm font-semibold text-gray-500">{selectedThreadIds.length} thread(s) selected</span>
        </div>
      </section>
      <section className="bg-white rounded-[2rem] shadow-lg border border-orange-100 overflow-hidden">
        {loading ? (
          <div className="p-6 text-gray-500">Loading enquiries...</div>
        ) : conversations.length === 0 ? (
          <div className="p-6 text-gray-500">No enquiries found.</div>
        ) : (
          <div className="divide-y divide-orange-100">
            {conversations.map((conversation) => {
              const isAdminThread = conversation.collaborator_id === 'admin' || conversation.status === 'admin_note';
              const replyText = replyTextByThread[conversation.id] || '';
              const firstMessage = (conversation.messages || [])[0];
              const senderName =
                firstMessage?.sender_name ||
                (isAdminThread ? 'Kala Manch Admin' : conversation.collaborator_name || conversation.artist_name || 'Unknown sender');
              const senderType = firstMessage?.sender_type || (isAdminThread ? 'admin' : 'collaborator');
              const createdAt = firstMessage?.created_at || conversation.created_at || conversation.updated_at;
              const latestAt = conversation.updated_at || createdAt;
              const selectedMessageIds = selectedMessageIdsByThread[conversation.id] || [];
              const messageIds = (conversation.messages || []).map((entry) => entry.id).filter(Boolean);
              const allMessagesSelected = messageIds.length > 0 && messageIds.every((id) => selectedMessageIds.includes(id));

              return (
              <div key={conversation.id} className="p-5 sm:p-6 space-y-4">
                <div className="flex flex-col xl:flex-row xl:items-start xl:justify-between gap-4">
                  <div className="space-y-3 flex-1">
                    <div className="flex flex-wrap gap-3 items-center">
                      <input
                        type="checkbox"
                        checked={selectedThreadIds.includes(conversation.id)}
                        onChange={(event) => toggleThreadSelection(conversation.id, event.target.checked)}
                        className="h-4 w-4 accent-orange-600"
                        aria-label="Select PMB thread"
                      />
                      <h3 className="text-xl font-semibold text-gray-900">{conversation.subject}</h3>
                      <span className={`${isAdminThread ? 'bg-purple-100 text-purple-700' : 'bg-orange-100 text-[#FF6F00]'} px-3 py-1 rounded-full text-xs font-semibold`}>
                        {isAdminThread ? 'Admin profile review' : conversation.status}
                      </span>
                    </div>
                    <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-5 rounded-2xl border border-orange-100 bg-orange-50/40 p-4">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">Sent</p>
                        <p className="mt-1 text-sm font-medium text-gray-800">{formatTimestamp(createdAt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">Latest</p>
                        <p className="mt-1 text-sm font-medium text-gray-800">{formatTimestamp(latestAt)}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">Sender</p>
                        <p className="mt-1 text-sm font-medium text-gray-800">{senderName}</p>
                        <p className="text-xs text-gray-500 capitalize">{senderType}</p>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">Artist</p>
                        <Link to={artistProfilePath({ id: conversation.artist_id, name: conversation.artist_name })} className="mt-1 inline-flex text-sm font-medium text-orange-700 hover:text-orange-800">
                          {conversation.artist_name || 'View artist'}
                        </Link>
                      </div>
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-gray-500">{isAdminThread ? 'Thread type' : 'Collaborator'}</p>
                        {isAdminThread ? (
                          <p className="mt-1 text-sm font-medium text-gray-800">Admin review</p>
                        ) : (
                          <Link
                            to={collaboratorProfilePath({
                              id: conversation.collaborator_id,
                              organization_name: conversation.collaborator_name,
                            })}
                            className="mt-1 inline-flex text-sm font-medium text-teal-700 hover:text-teal-800"
                          >
                            {conversation.collaborator_name || 'View collaborator'}
                          </Link>
                        )}
                      </div>
                    </div>
                    <p className="text-sm text-gray-500">Messages: {conversation.messages?.length || 0}</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      className="btn-outline"
                      onClick={() => setExpandedConversationId((prev) => (prev === conversation.id ? '' : conversation.id))}
                    >
                      {expandedConversationId === conversation.id ? 'Hide Messages' : 'View Messages'}
                    </button>
                    {showDeleted ? (
                      <>
                        <button className="btn-outline border-emerald-300 text-emerald-700 hover:bg-emerald-50" onClick={() => handleRestore(conversation.id)}>Undo Delete</button>
                        <button className="btn-outline border-red-300 text-red-600 hover:bg-red-50" onClick={() => handlePermanentDelete(conversation.id)}>Permanent Delete</button>
                      </>
                    ) : (
                      <button className="btn-outline border-red-300 text-red-600 hover:bg-red-50" onClick={() => handleDelete(conversation.id)}>Delete Thread</button>
                    )}
                  </div>
                </div>
                {expandedConversationId === conversation.id && (
                  <div className="rounded-2xl border border-orange-100 bg-orange-50/40 p-4 sm:p-5 space-y-3">
                    {(conversation.messages || []).length === 0 ? (
                      <p className="text-sm text-gray-500">No messages in this thread yet.</p>
                    ) : (
                      <>
                        <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-orange-100 bg-white p-3">
                          <button
                            type="button"
                            className="btn-outline text-sm px-4 py-2"
                            onClick={() => toggleAllMessages(conversation.id, conversation.messages)}
                          >
                            {allMessagesSelected ? 'Clear Selection' : 'Select All Messages'}
                          </button>
                          <button
                            type="button"
                            className="btn-outline border-red-300 text-red-600 hover:bg-red-50 text-sm px-4 py-2"
                            onClick={() => handleHideSelectedMessages(conversation.id)}
                            disabled={busyHideMessagesId === conversation.id || selectedMessageIds.length === 0}
                          >
                            {busyHideMessagesId === conversation.id ? 'Hiding...' : `Hide Selected (${selectedMessageIds.length})`}
                          </button>
                          <p className="text-xs text-gray-500">
                            Admin hide only. Artist, collaborator, website, and app inboxes keep these messages.
                          </p>
                        </div>
                        {conversation.messages.map((entry) => (
                          <div key={entry.id} className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedMessageIds.includes(entry.id)}
                              onChange={(event) => toggleMessageSelection(conversation.id, entry.id, event.target.checked)}
                              className="mt-4 h-4 w-4 accent-orange-600"
                              aria-label="Select message for admin hide"
                            />
                            <div
                              className={`max-w-3xl flex-1 rounded-2xl px-4 py-3 ${
                                entry.sender_type === 'admin'
                                  ? 'bg-purple-100 text-gray-800'
                                  : entry.sender_type === 'artist'
                                    ? 'bg-[#FFEDD5] text-gray-800'
                                    : 'bg-[#E6FFFB] text-gray-800'
                              }`}
                            >
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-xs font-semibold uppercase tracking-wide text-gray-600">{entry.sender_type}</span>
                                <span className="text-sm font-semibold text-gray-900">{entry.sender_name}</span>
                              </div>
                              <p className="text-sm">{entry.message}</p>
                              <p className="text-[11px] text-gray-500 mt-2">{new Date(entry.created_at).toLocaleString()}</p>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                    {isAdminThread && (
                      <div className="rounded-2xl border border-purple-100 bg-white p-4 space-y-3">
                        <p className="text-sm font-semibold text-purple-800">Reply to artist profile-review thread</p>
                        <textarea
                          value={replyText}
                          onChange={(event) =>
                            setReplyTextByThread((current) => ({
                              ...current,
                              [conversation.id]: event.target.value,
                            }))
                          }
                          rows="3"
                          placeholder="Write the next admin note for this artist..."
                          className="w-full rounded-2xl border border-purple-200 px-4 py-3"
                        />
                        <button
                          type="button"
                          className="btn-primary"
                          onClick={() => handleAdminReply(conversation.id)}
                          disabled={busyReplyId === conversation.id || !replyText.trim()}
                        >
                          {busyReplyId === conversation.id ? 'Sending...' : 'Send Admin Reply'}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminEnquiries;
